import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { formatCurrency } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ArrowLeft, GitBranch, MapPin, Phone, Mail, UserCog, Globe, Users, UserPlus,
  Briefcase, Handshake, Dumbbell, Newspaper, DollarSign, TrendingUp,
} from "lucide-react";
import { toast } from "sonner";

const StatCard = ({ icon: Icon, label, value, accent, to, navigate, testid }) => (
  <Card onClick={to ? () => navigate(to) : undefined}
    className={`p-4 border-[#E6EDF2] ${to ? "cursor-pointer transition-shadow hover:shadow-md" : ""}`}
    style={{ boxShadow: "var(--mmm-shadow-sm)" }} data-testid={testid}>
    <div className="flex items-center gap-3">
      <span className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: `${accent}1A` }}>
        <Icon size={19} style={{ color: accent }} />
      </span>
      <div className="min-w-0">
        <div className="text-xl font-bold font-display tabular-nums text-[#252221] leading-none">{value ?? 0}</div>
        <div className="text-[11px] text-muted-foreground mt-1 truncate">{label}</div>
      </div>
    </div>
  </Card>
);

export default function BranchDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/branches/${id}/overview`);
      setData(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load branch");
      navigate("/branches");
    } finally { setLoading(false); }
  }, [id, navigate]);
  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div className="space-y-4"><Skeleton className="h-8 w-40" /><Skeleton className="h-32 w-full" /><div className="grid grid-cols-2 lg:grid-cols-4 gap-3">{[...Array(8)].map((_, i) => <Skeleton key={i} className="h-20" />)}</div></div>;
  }
  if (!data) return null;
  const b = data.branch;
  const s = data.stats || {};

  return (
    <div>
      <button onClick={() => navigate("/branches")} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-[#1C4358] mb-4" data-testid="back-to-branches">
        <ArrowLeft size={15} /> Back to branches
      </button>

      {/* Branch header */}
      <Card className="p-5 border-[#E6EDF2] mb-5" style={{ boxShadow: "var(--mmm-shadow-sm)" }} data-testid="branch-header">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="h-14 w-14 rounded-2xl flex items-center justify-center shrink-0" style={{ backgroundColor: "rgba(28,67,88,0.10)" }}>
              <GitBranch size={26} className="text-[#1C4358]" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="font-display text-2xl font-bold text-[#252221]">{b.branch_name || b.name}</h2>
                {b.is_pilot && <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold" style={{ backgroundColor: "rgba(222,224,41,0.35)", color: "#5A5E2E" }}>PILOT</span>}
                <StatusBadge value={b.status} />
              </div>
              <div className="text-sm text-muted-foreground flex items-center gap-1 mt-1"><MapPin size={14} /> {[b.city_region || b.city, b.state_province || b.state, b.country].filter(Boolean).join(", ")}</div>
              <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 text-sm text-muted-foreground">
                {data.manager && <span className="flex items-center gap-1.5"><UserCog size={13} /> {data.manager.full_name}</span>}
                {b.phone && <span className="flex items-center gap-1.5"><Phone size={13} /> {b.phone}</span>}
                {b.email && <span className="flex items-center gap-1.5"><Mail size={13} /> {b.email}</span>}
                {b.service_area && <span className="flex items-center gap-1.5"><Globe size={13} /> {b.service_area}</span>}
              </div>
            </div>
          </div>
        </div>
        {b.notes && <p className="text-sm text-[#252221] mt-4 pt-4 border-t border-[#E6EDF2]">{b.notes}</p>}
      </Card>

      {/* Local stats */}
      <h3 className="font-display text-lg font-bold text-[#252221] mb-3">Local activity</h3>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5" data-testid="branch-stats">
        <StatCard icon={Users} label="Team members" value={s.team_members} accent="#1C4358" navigate={navigate} testid="stat-team" />
        <StatCard icon={UserPlus} label="Local leads" value={s.leads} accent="#1C4358" to="/leads" navigate={navigate} testid="stat-leads" />
        <StatCard icon={Briefcase} label="Local cases" value={s.cases} accent="#9A6516" to="/cases" navigate={navigate} testid="stat-cases" />
        <StatCard icon={Handshake} label="Local partners" value={s.partners} accent="#1C4358" to="/partners" navigate={navigate} testid="stat-partners" />
        <StatCard icon={Dumbbell} label="Trilogy outreach" value={s.trilogy_outreach} accent="#256B4B" to="/trilogy-outreach" navigate={navigate} testid="stat-trilogy" />
        <StatCard icon={Newspaper} label="Newsletter subs" value={s.newsletter_subscribers} accent="#4B5563" testid="stat-newsletter" />
        <StatCard icon={DollarSign} label="Revenue opps" value={s.revenue_opportunities} accent="#2F7D5A" to="/revenue" navigate={navigate} testid="stat-revenue-opps" />
        <StatCard icon={TrendingUp} label="Realized revenue" value={formatCurrency(s.revenue_realized || 0)} accent="#2F7D5A" testid="stat-revenue-realized" />
      </div>

      {/* Revenue summary */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Card className="p-4 border-[#E6EDF2]" style={{ boxShadow: "var(--mmm-shadow-sm)" }} data-testid="branch-revenue-realized">
          <div className="text-xs text-muted-foreground">Realized revenue</div>
          <div className="text-2xl font-bold font-display tabular-nums text-[#2F7D5A] mt-1">{formatCurrency(s.revenue_realized || 0)}</div>
        </Card>
        <Card className="p-4 border-[#E6EDF2]" style={{ boxShadow: "var(--mmm-shadow-sm)" }} data-testid="branch-revenue-pipeline">
          <div className="text-xs text-muted-foreground">Revenue in pipeline</div>
          <div className="text-2xl font-bold font-display tabular-nums text-[#252221] mt-1">{formatCurrency(s.revenue_pipeline || 0)}</div>
        </Card>
      </div>
    </div>
  );
}
